import { AsyncPipe, TitleCasePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, of, take, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { SchoolMemberService } from '../../../../services/member.school.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import * as schoolMemberSelector from '../../../../state/selectors/member.school.selector';
import * as paginationSelector from '../../../../state/selectors/pagination.selector';
import {
  BtnFabComponent,
  BtnFabPosition,
} from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { ListUtilsBarComponent } from '../../../components/list-utils-bar/list-utils-bar.component';
import { LoadingMemberComponent } from '../../../components/loaders/loading-member/loading-member.component';
import { DlgAssignRolesComponent } from '../../dlg-assign-roles/dlg-assign-roles.component';
import { ChangeMemberStatusComponent } from '../change-member-status/change-member-status.component';

@Component({
  selector: 'app-list-unassigned',
  standalone: true,
  imports: [
    BtnMoreComponent,
    AsyncPipe,
    TitleCasePipe,
    MatDialogModule,
    BtnFabComponent,
    ListUtilsBarComponent,
    ChangeMemberStatusComponent,
    LoadingMemberComponent,
    InitialsPipe,
  ],
  templateUrl: './list-unassigned.component.html',
  styleUrl: './list-unassigned.component.css',
})
export class ListUnassignedComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  firstNewItemId: string | null = null;
  private cdr = inject(ChangeDetectorRef);
  @ViewChildren('itemRef') itemRefs!: QueryList<ElementRef>;

  route: ActivatedRoute = inject(ActivatedRoute);
  roles: SchoolRolesService = inject(SchoolRolesService);
  memberService: SchoolMemberService = inject(SchoolMemberService);
  private dialog: MatDialog = inject(MatDialog);

  headers: string[] = ['Name', 'Request Date', 'Actions'];

  members$: Observable<SchoolMember[] | null | undefined> = new Observable();

  loading$: Observable<boolean> = new Observable();
  isSearching$: Observable<boolean> = new Observable();
  showingSearchResults$: Observable<boolean> = new Observable();

  groupId: string | null = this.route.snapshot.paramMap.get('id');

  group: Group | null | undefined;

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  rolesType = SchoolRoles;
  utils = UtilsService;
  btnFabPostion = BtnFabPosition;

  myRoles$: Observable<any> = new Observable();

  exit$: Subject<void> = new Subject<void>();

  inviteText: string =
    'Hi, I would like to invite you to join our school group on AtChurch. Please click on the link below to join. (https://web.atchurch.app/links/groupinvite?groupId=';

  constructor(private store: Store<AppState>) {
    this.route.paramMap.subscribe((params) => {
      this.groupId = params.get('id');

      this.loading$ = this.store.select(schoolMemberSelector.loading);

      this.myRoles$ = this.store.select(
        groupSelector.getMyRolesInGroup(this.groupId),
      );

      this.store
        .select(groupSelector.getGroupById(this.groupId!))
        .pipe(takeUntil(this.exit$))
        .subscribe((group) => {
          if (group == undefined) return;
          this.group = group;

          this.loadMembers();

          this.inviteText = `Hi, I would like to invite you to join our school group '${this.group.name}' on AtChurch. Please click on the link below to join. https://${environment.domain}/links/groupinvite?groupId=`;
        });
    });
  }

  ngOnInit(): void {
    if (this.groupId) {
      this.members$.pipe(take(1)).subscribe((members) => {
        if (members?.length === 0) {
          this.store.dispatch(
            memberActions.getMembers({
              source: 'ListUnassignedComponent :: ngOnInit',
              groupId: this.groupId!,
              status: [
                MembershipStatus.requested,
                MembershipStatus.invited,
                MembershipStatus.rejected,
              ],
            }),
          );
        }
      });
    }
  }

  ngAfterViewInit() {
    this.itemRefs.changes.subscribe(() => {
      if (this.firstNewItemId) {
        const el = this.itemRefs.find(
          (el) =>
            el.nativeElement.getAttribute('data-id') === this.firstNewItemId,
        );
        if (el) {
          el.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });

          requestAnimationFrame(() => {
            this.firstNewItemId = null;
            this.cdr.detectChanges();

            setTimeout(() => {
              this.firstNewItemId = null;
              this.cdr.detectChanges();
            }, 2000);
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  loadMembers() {
    this.members$ = this.store.pipe(
      select(
        schoolMemberSelector.getGroupMembersByStatus(this.groupId!, [
          MembershipStatus.invited,
          MembershipStatus.requested,
          MembershipStatus.rejected,
          MembershipStatus.alumni,
        ]),
      ),
    );
  }

  addMore() {
    this.store
      .select(paginationSelector.getPaginationById(this.groupId!))
      .pipe(take(1))
      .subscribe((lastRecord) => {
        if (lastRecord?.lastUnassignedRecord?.id) {
          this.firstNewItemId = lastRecord.lastUnassignedRecord.id;
        }

        this.store.dispatch(
          memberActions.getMembers({
            source: 'ListUnassignedComponent :: addMore',
            groupId: this.groupId!,
            getMemberType: [SchoolMemberType.unassigned],
            // same statuses the list shows (pending join requests)
            status: [
              MembershipStatus.requested,
              MembershipStatus.invited,
              MembershipStatus.rejected,
            ],
            lastRecord: lastRecord,
          }),
        );
      });
  }

  onAssignRole(item: SchoolMember) {
    let data: any = Object.values(SchoolMemberType);
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Update Member Role(s)',
      description: `Select one or more roles to assign to '${item.name}'`,
      action: 'Update Roles',
      member: item,
    };
    const dialogRef = this.dialog.open(DlgAssignRolesComponent, dialogConfig);
  }

  onSearchTermChange(value: string) {
    this.store
      .select(
        memberSelector.getGroupMembersByType({
          memberType: [SchoolMemberType.unassigned],
          groupId: this.groupId,
          status: [
            MembershipStatus.requested,
            MembershipStatus.invited,
            MembershipStatus.rejected,
          ],
          filter: value,
        }),
      )
      .pipe(take(1))
      .subscribe((items) => {
        this.members$ = of(items);
      });
  }

  async onSearch(term: string) {
    this.members$ = of([]);
    this.isSearching$ = of(true);
    const result = await this.memberService.searchMembers(term, this.groupId!, {
      memberType: [SchoolMemberType.unassigned],
    });
    this.members$ = of(result);
    this.isSearching$ = of(false);
    this.showingSearchResults$ = of(true);
  }

  onCancelSearch() {
    this.loadMembers();
    this.isSearching$ = of(false);
    this.showingSearchResults$ = of(false);
  }

  onRefresh(): void {
    this.store.dispatch(
      memberActions.getMembers({
        source: 'ListUnassignedComponent :: onRefresh',
        groupId: this.groupId!,
        status: [
          MembershipStatus.requested,
          MembershipStatus.invited,
          MembershipStatus.rejected,
        ],
      }),
    );
  }
}
